import { useAlert } from '@ads/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getApiV1ExamRevisionBotexamidQueryKey,
  usePostApiV1ExamRevisionLangBotexamlangidQuestion,
} from '@/shared/api/generated';

/**
 * Adiciona uma nova questão à prova via
 * `POST /api/v1/exam/revision/lang/{botExamLangId}/question` (202, **sem corpo**).
 * O backend cria uma questão vazia com 4 alternativas (a primeira correta),
 * replicada para os demais idiomas. Ao concluir, recarrega a revisão
 * (`GET /{botExamId}`) para expor a questão recém-criada, que é então preenchida
 * manualmente e persistida via `PUT /api/v1/exam/revision/question`.
 *
 * @param botExamId - ID da prova bot em revisão (para invalidação/recarga)
 * @returns Handler de adição e estado de loading
 *
 * @example
 * ```tsx
 * const { addQuestion, isLoading } = useAddRevisionQuestion(botExamId);
 * addQuestion(botExamLangId);
 * ```
 */
export function useAddRevisionQuestion(botExamId: number) {
  const queryClient = useQueryClient();
  const { alert } = useAlert();
  const { t } = useTranslation('examRevision');

  const mutation = usePostApiV1ExamRevisionLangBotexamlangidQuestion({
    mutation: {
      onSuccess: () => {
        alert({ alertType: 'success', title: t('question.toast.addSuccess') });
        void queryClient.invalidateQueries({
          queryKey: getApiV1ExamRevisionBotexamidQueryKey(botExamId),
        });
      },
      onError: () => alert({ alertType: 'warning', title: t('question.toast.addError') }),
    },
  });

  const addQuestion = (botExamLangId: number, callbacks?: { onSuccess?: () => void }) => {
    mutation.mutate(
      { botExamLangId },
      callbacks?.onSuccess ? { onSuccess: callbacks.onSuccess } : undefined,
    );
  };

  return { addQuestion, isLoading: mutation.isPending } as const;
}
